import { DotLegalBreadCrumbModel, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useAuditGroupHeader } from "./AuditGroupHeader.hooks";
import { useTranslation } from "../../../localization/useTranslation";
import CreateLegalEntityAuditGroupDialog from "../../createLegalEntityAuditGroupDialog/CreateLegalEntityAuditGroupDialog";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../useUrlProvider";
import { AuditGroupEditModel } from "./AuditGroupHeader.types";
import DeleteLegalEntityAuditGroupDialog from "../../deleteLegalEntityAuditGroupDialog/DeleteLegalEntityAuditGroupDialog";
import PageHeaderEditEntityButton, {
    PageHeaderEditEntityButtonMenuItem,
} from "../../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useAddTaskMenuItem } from "../../../common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import { EntityType } from "../../../common/Common.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";

export interface AuditGroupHeaderProps {
    breadCrumbs: Array<DotLegalBreadCrumbModel>;
    customerName: string;
    auditGroupModel: AuditGroupEditModel | undefined;
    onAuditGroupUpdated: () => void;
    showAddLegalEntityBtn: boolean;
    showDeleteMenuItem: boolean;
    canDeleteAuditgroup: boolean;
}

function AuditGroupHeader(props: AuditGroupHeaderProps) {
    const navigate = useNavigate();
    const { translateString } = useTranslation();
    const { showEditDialog, setShowEditDialog, id, showDeleteDialog, setShowDeleteDialog, onDownloadManagementReportClick, isDownloadingManagementReport } =
        useAuditGroupHeader(props);
    const { getAuditGroupDraftUrl, getAuditGroupsUrl, getAuditGroupManagementReportUrl } = useUrlProvider();
    const { vendorManagementFeatures } = usePlanContext();
    const addTaskMenuItem = useAddTaskMenuItem();
    const { permissions } = useUserContext();

    const getEditMenuItems = () => {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [];

        if (vendorManagementFeatures.auditGroups && permissions.canManageAuditGroups) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setShowEditDialog(true);
                },
            });

            if (props.showAddLegalEntityBtn) {
                menuItems.push({
                    menuItem: translateString("addLegalEntity"),
                    onClick: () => {
                        navigate(getAuditGroupDraftUrl(id!));
                    },
                });
            }

            if (props.showDeleteMenuItem) {
                menuItems.push({
                    menuItem: translateString("delete"),
                    onClick: () => {
                        setShowDeleteDialog(true);
                    },
                });
            }

            if (props.auditGroupModel && props.auditGroupModel.sent !== null) {
                menuItems.push({
                    menuItem: translateString("viewManagementReportInBrowser"),
                    onClick: () => {},
                    href: getAuditGroupManagementReportUrl(props.auditGroupModel.id),
                });

                menuItems.push({
                    menuItem: translateString("downloadManagementReport"),
                    onClick: async () => {
                        await onDownloadManagementReportClick();
                    },
                });

                addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
                    fromEntityId: id!,
                    fromEntityType: EntityType.LegalEntityAuditGroup,
                    toEntityType: EntityType.Task,
                });
            }
        }

        return menuItems;
    };

    return (
        <React.Fragment>
            <DotLegalPageHeader
                breadCrumbs={props.breadCrumbs}
                rightComponent={<PageHeaderEditEntityButton menuItems={getEditMenuItems()} showSpinner={isDownloadingManagementReport} />}
            />

            {props.auditGroupModel && showEditDialog && (
                <CreateLegalEntityAuditGroupDialog
                    onDialogClose={() => setShowEditDialog(false)}
                    auditGroup={{
                        description: props.auditGroupModel.description,
                        name: props.auditGroupModel.name,
                        deadline: props.auditGroupModel.deadline,
                        groupEntityId: props.auditGroupModel.senderId,
                        questionnaireId: props.auditGroupModel.auditTemplateId,
                        questionnaireName: props.auditGroupModel.auditTemplateName,
                        questionnaireVersion: props.auditGroupModel.auditTemplateVersion,
                        responsibleId: props.auditGroupModel.responsibleId,
                        questionnaireIntroductionTitle: props.auditGroupModel.questionnaireIntroductionTitle,
                        relations: [],
                    }}
                    onAuditGroupUpdated={() => {
                        setShowEditDialog(false);
                        props.onAuditGroupUpdated();
                    }}
                    auditGroupId={props.auditGroupModel.id}
                    auditGroupInProgress={props.auditGroupModel.sent !== undefined && props.auditGroupModel.sent !== null}
                />
            )}

            {props.auditGroupModel && showDeleteDialog && (
                <DeleteLegalEntityAuditGroupDialog
                    auditGroupId={id!}
                    auditGroupName={props.auditGroupModel.name}
                    deadline={props.auditGroupModel.deadline}
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeletedAuditGroup={() => navigate(getAuditGroupsUrl())}
                    canDeleteAuditGroup={props.canDeleteAuditgroup}
                />
            )}
        </React.Fragment>
    );
}

export default AuditGroupHeader;
